import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, Upload, X } from "lucide-react";
import toast from "react-hot-toast";
import { DsButton, DsIcon, DsLabel } from "../../../design-system";
import {
  branchService,
  categoryService,
  departmentService,
  designationService,
  gradesService,
  reasonsService,
  subDepartmentService,
} from "../../../services/api";
import {
  ImportValidationReview,
  buildImportValidationResult,
  downloadValidationStatusWorkbook,
} from "../../Common/import-validation";
import {
  MASTER_IMPORT_ORDER,
  MASTER_TEMPLATE_OPTIONS,
  downloadMastersImportTemplate,
  parseMastersWorkbook,
} from "./mastersImportTemplate";
import { validateMastersRows } from "./mastersImportValidate";

function asList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function errMessage(err) {
  return err?.response?.data?.message || err?.message || "Request failed";
}

/**
 * Import / Export → Configuration → Import Masters
 * Validate → Success/Failed tabs → consent Import all | Ignore failed & import.
 */
export default function MastersImportModal({ isOpen, onClose, onImported }) {
  const fileRef = useRef(null);
  const [templateKey, setTemplateKey] = useState("all");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resultNote, setResultNote] = useState("");
  const [departmentNames, setDepartmentNames] = useState([]);
  const [review, setReview] = useState(null);
  const [pendingCreates, setPendingCreates] = useState([]);

  const resetReview = () => {
    setReview(null);
    setPendingCreates([]);
  };

  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setResultNote("");
    resetReview();
    setBusy(false);
    setTemplateKey("all");
    if (fileRef.current) fileRef.current.value = "";
    let cancelled = false;
    (async () => {
      try {
        const res = await departmentService.getDepartments(true);
        if (!cancelled) {
          setDepartmentNames(
            asList(res)
              .map((d) => d.name)
              .filter(Boolean)
          );
        }
      } catch {
        if (!cancelled) setDepartmentNames([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const templateLabel = useMemo(
    () => MASTER_TEMPLATE_OPTIONS.find((o) => o.key === templateKey)?.label || templateKey,
    [templateKey]
  );

  if (!isOpen || typeof document === "undefined") return null;

  const handleDownload = async () => {
    try {
      setBusy(true);
      await downloadMastersImportTemplate(templateKey, { departmentNames });
      toast.success("Template downloaded");
    } catch (err) {
      toast.error(err?.message || "Failed to download template");
    } finally {
      setBusy(false);
    }
  };

  const loadExisting = async () => {
    const safe = (p) =>
      Promise.resolve(typeof p === "function" ? p() : p)
        .then((r) => asList(r))
        .catch(() => []);
    const [
      branches,
      departments,
      grades,
      categories,
      termReasons,
      resignReasons,
      subDepartments,
      designations,
    ] = await Promise.all([
      safe(() => branchService.getBranches(true)),
      safe(() => departmentService.getDepartments(true)),
      safe(() => gradesService.getGrades(true)),
      safe(() => categoryService.getAllCategories(true)),
      safe(() => reasonsService.getAllTerminationReasons(true)),
      safe(() => reasonsService.getAllResignationReasons(true)),
      safe(() => subDepartmentService.getAllSubDepartments(true)),
      safe(() => designationService.getDesignations(true)),
    ]);
    return {
      branches,
      departments,
      grades,
      categories,
      reasons: [...termReasons, ...resignReasons],
      subDepartments,
      designations,
    };
  };

  const handleValidate = async () => {
    if (!file) {
      toast.error("Please choose a file first.");
      return;
    }
    setBusy(true);
    setResultNote("");
    resetReview();
    try {
      const buf = await file.arrayBuffer();
      const bySheet = await parseMastersWorkbook(buf);
      const sheetsPresent = MASTER_IMPORT_ORDER.filter(
        (s) => (bySheet[s] || []).length > 0
      );
      if (!sheetsPresent.length) {
        toast.error("No master data rows found. Use the PaceHRM template sheets.");
        setResultNote("No data rows found.");
        return;
      }

      let existing = {};
      try {
        existing = await loadExisting();
      } catch {
        existing = {};
      }

      const { validRecords, invalidRecords } = validateMastersRows(bySheet, existing);
      const built = buildImportValidationResult({ validRecords, invalidRecords });
      setPendingCreates(validRecords);
      setReview(built);
      if (built.totalCount === 0) {
        toast.error("No data rows found.");
      } else if (built.failedCount > 0) {
        toast(
          `Validated: ${built.successCount} successful, ${built.failedCount} failed`
        );
      } else {
        toast.success(`All ${built.successCount} row(s) passed validation`);
      }
    } catch (err) {
      toast.error(errMessage(err));
      setResultNote(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const resolveDeptId = (name, deptByName) => {
    const key = String(name || "").trim().toLowerCase();
    if (!key) return null;
    return deptByName.get(key) || null;
  };

  const runImport = async ({ ignoreFailed }) => {
    if (!review) return;
    if (!ignoreFailed && review.failedCount > 0) {
      toast.error("Fix failed rows or choose Ignore failed & import.");
      return;
    }
    const toCreate = pendingCreates.filter(Boolean);
    if (!toCreate.length) {
      toast.error("No successful rows to import");
      return;
    }

    setBusy(true);
    setResultNote("");
    let created = 0;
    const runtimeErrors = [];

    try {
      const deptRes = await departmentService.getDepartments(true);
      const deptByName = new Map();
      asList(deptRes).forEach((d) => {
        if (d?.name) deptByName.set(String(d.name).trim().toLowerCase(), d.id);
      });

      // Process in master FK order
      const ordered = [...toCreate].sort(
        (a, b) =>
          MASTER_IMPORT_ORDER.indexOf(a.sheetName) -
          MASTER_IMPORT_ORDER.indexOf(b.sheetName)
      );

      for (const item of ordered) {
        const row = item.normalizedRecord || item;
        const sheetName = row.sheetName || item.sheetName;
        const line = `${sheetName} row ${item.row || row._row}`;

        try {
          if (sheetName === "Branch") {
            await branchService.createBranch({
              name: row.name,
              address: row.address || "",
              state: row._state || row.state,
              status: row._status || "Active",
            });
            created += 1;
          } else if (sheetName === "Department") {
            const res = await departmentService.createDepartment({
              name: row.name,
              status: row._status || "Active",
            });
            const id = res?.data?.id || res?.id;
            if (id) {
              deptByName.set(String(row.name).trim().toLowerCase(), id);
            }
            created += 1;
          } else if (sheetName === "Grade") {
            await gradesService.createGrade({
              name: row.name,
              status: row._status || "Active",
            });
            created += 1;
          } else if (sheetName === "Category") {
            await categoryService.createCategory({
              name: row.name,
              status: row._status || "Active",
            });
            created += 1;
          } else if (sheetName === "Reasons") {
            await reasonsService.createReason({
              name: row.name,
              type: row._type || row.type,
              status: row._status || "Active",
            });
            created += 1;
          } else if (sheetName === "Sub Department") {
            const deptId =
              row._deptId || resolveDeptId(row.department, deptByName);
            if (!deptId) {
              runtimeErrors.push({
                line,
                message: `Department “${row.department}” not found`,
              });
              continue;
            }
            await subDepartmentService.createSubDepartment({
              name: row.name,
              departmentId: deptId,
              status: row._status || "Active",
            });
            created += 1;
          } else if (sheetName === "Designation") {
            const deptId =
              row._deptId || resolveDeptId(row.department, deptByName);
            if (!deptId) {
              runtimeErrors.push({
                line,
                message: `Department “${row.department}” not found`,
              });
              continue;
            }
            await designationService.createDesignation({
              name: row.name,
              departmentId: deptId,
              status: row._status || "Active",
              salaryRangeFrom: row.salaryRangeFrom,
              salaryRangeTo: row.salaryRangeTo,
              experience: row.experience || "",
              qualifications: row.qualifications || "",
              jobDescription: row.jobDescription || "",
            });
            created += 1;
          }
        } catch (err) {
          runtimeErrors.push({ line, message: errMessage(err) });
        }
      }

      const note = `Created ${created}.${
        runtimeErrors.length
          ? ` ${runtimeErrors.length} failed at write time.`
          : ""
      }`;
      setResultNote(note);
      if (created > 0) {
        toast.success(`Imported ${created} master record(s)`);
        onImported?.({ created, errors: runtimeErrors });
        resetReview();
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
        if (!runtimeErrors.length) onClose?.();
      } else {
        toast.error(runtimeErrors[0]?.message || "No rows imported");
      }
    } catch (err) {
      toast.error(errMessage(err));
      setResultNote(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!review || !file) {
      toast.error("Validate a file first.");
      return;
    }
    try {
      setBusy(true);
      await downloadValidationStatusWorkbook(
        file,
        review.allRows,
        "Masters_Validation_Status.xlsx"
      );
      toast.success("Validation report downloaded");
    } catch (err) {
      toast.error(err?.message || "Failed to download report");
    } finally {
      setBusy(false);
    }
  };

  const fieldClass =
    "w-full rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm text-[hsl(var(--ds-fg))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ds-primary))]/30";

  const showReview = !!review;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="masters-import-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose?.();
      }}
    >
      <div
        className={`flex max-h-[90vh] w-full flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-xl ${
          showReview ? "max-w-4xl" : "max-w-lg"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 border-b border-[hsl(var(--ds-border))] px-5 py-4">
          <div>
            <h3
              id="masters-import-title"
              className="text-base font-semibold text-[hsl(var(--ds-fg))]"
            >
              Import Masters
            </h3>
            {!showReview ? (
              <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">
                Download the Excel template, fill rows, then Validate. Import runs only
                after you consent.
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="rounded-md p-1 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
            onClick={() => !busy && onClose?.()}
            aria-label="Close"
          >
            <DsIcon icon={X} size="sm" />
          </button>
        </div>

        {showReview ? (
          <div className="flex min-h-0 flex-1 flex-col px-5 py-4">
            <ImportValidationReview
              result={review}
              busy={busy}
              title="Masters validation"
              entityLabel="master"
              onBack={() => {
                resetReview();
                setResultNote("");
              }}
              onDownloadReport={handleDownloadReport}
              onImportAll={() => runImport({ ignoreFailed: false })}
              onImportSuccessfulOnly={() => runImport({ ignoreFailed: true })}
            />
            {resultNote ? (
              <p className="mt-2 text-xs text-[hsl(var(--ds-muted-fg))]">{resultNote}</p>
            ) : null}
          </div>
        ) : (
          <>
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
              <div>
                <DsLabel htmlFor="masters-template">Master template</DsLabel>
                <select
                  id="masters-template"
                  value={templateKey}
                  onChange={(e) => setTemplateKey(e.target.value)}
                  className={`${fieldClass} mt-1`}
                  disabled={busy}
                >
                  {MASTER_TEMPLATE_OPTIONS.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                  Selected:{" "}
                  <span className="font-semibold text-[hsl(var(--ds-fg))]">
                    {templateLabel}
                  </span>
                  {templateKey === "all"
                    ? " — one workbook with every master sheet."
                    : " — single-sheet template."}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <DsButton
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={handleDownload}
                >
                  <DsIcon icon={Download} size="sm" />
                  Download Template
                </DsButton>
              </div>

              <div>
                <DsLabel htmlFor="masters-file">Upload filled file</DsLabel>
                <input
                  id="masters-file"
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xls"
                  disabled={busy}
                  className={`${fieldClass} mt-1`}
                  onChange={(e) => {
                    setFile(e.target.files?.[0] || null);
                    setResultNote("");
                    resetReview();
                  }}
                />
                {file ? (
                  <p className="mt-1 truncate text-[11px] text-[hsl(var(--ds-muted-fg))]">
                    {file.name}
                  </p>
                ) : null}
              </div>

              <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                Validate first. You will see successful and failed rows, then choose to
                import all (only if none failed) or ignore failed and import successful
                rows.
              </p>

              {resultNote ? (
                <p className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40 px-3 py-2 text-xs text-[hsl(var(--ds-fg))]">
                  {resultNote}
                </p>
              ) : null}
            </div>

            <div className="flex flex-wrap justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-5 py-3">
              <DsButton
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={onClose}
              >
                Cancel
              </DsButton>
              <DsButton
                type="button"
                size="sm"
                disabled={busy || !file}
                onClick={handleValidate}
              >
                <DsIcon icon={Upload} size="sm" />
                {busy ? "Validating…" : "Validate"}
              </DsButton>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
